import { useCallback } from 'react';

import { ChannelContent } from 'src/components/chat/inner-channel/channel-content/ChannelContent';
import { useAIAssistantStatus } from 'src/features/assistant/useAIAssistantStatus';
import { useTypingStatus } from 'src/features/messages/useTypingStatus';
import { useAppSelector } from 'src/hooks/useStore';
import {
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';

interface InternalChatContentProps {
  placeholderText?: string;
  messageId?: string;
  isInternalChat?: boolean;
  setAIAssistantStatus?: (
    channelId: string,
    status: string,
    metadata?: { tool?: string; query?: string },
  ) => void;
  clearAIAssistantStatus?: () => void;
}

/**
 * Content component that must be rendered inside a ChannelProvider.
 * It retrieves AI assistant and typing status and renders ChannelContent.
 */
export const InternalChatContent = (props: InternalChatContentProps) => {
  // Get status data using hooks - these work because we're inside ChannelProvider
  const aiAssistantStatus = useAIAssistantStatus();
  const typingStatus = useTypingStatus();

  // Get entity data from Redux for name resolution
  const clientsMap = useAppSelector(selectClientsMap);
  const companiesMap = useAppSelector(selectAllCompaniesMap);

  // Create lookup functions for entity name resolution
  const getCompanyFromId = useCallback(
    (companyId: string) => {
      const company = companiesMap.get(companyId);
      if (!company) return null;
      return { name: company.fields.name };
    },
    [companiesMap],
  );

  const getClientFromId = useCallback(
    (clientId: string) => {
      const client = clientsMap.get(clientId);
      if (!client) return null;
      // Combine given name and family name for display
      const name =
        [client.fields.givenName, client.fields.familyName]
          .filter(Boolean)
          .join(' ') || client.fields.email;
      return { name };
    },
    [clientsMap],
  );

  return (
    <ChannelContent
      {...props}
      aiAssistantStatus={aiAssistantStatus}
      typingStatus={typingStatus}
      getCompanyFromId={getCompanyFromId}
      getClientFromId={getClientFromId}
    />
  );
};
